import React, { useEffect } from 'react'
import { readRemoteFile } from 'react-papaparse'
import * as XLSX from 'xlsx'
import { useCache } from '../../contexts/CacheContext'
import './styles.scss'
import './tableviewer.scss'
import LoadingBars from '../spinner/horizontalLoader'

const TableViewer = ({ url, format='.csv' }) => {
  const { fetchData, getCacheState } = useCache()
  const { isLoading, data, error } = getCacheState(url)

  useEffect(() => {
    fetchData(url, async (finalUrl) => {
      try {
        if(format === '.csv') {
          var { results } = await readTableFromURL({ url: finalUrl })
        } else if(format === '.xls' || format === '.xlsx') {
          var { results } = await readExcelFromURL({ url: finalUrl })
        }
        return results
      } catch(err) {
        console.error(err.error)
        throw err?.error || 'Error while reading table data from URL'
      }
    })
  }, [url, fetchData])

  // if (!data) return null
  if (!data && !isLoading) {
    return (
      <>
        <div className='sv3-prvw-title'>
          <h2>
            Table Preview Not Found
          </h2>
        </div>
      </>
    )
  }

  return (
    <>
      <div className='sv3-prvw-title'>
        <h2>
          Table Preview { isLoading && <>&nbsp;&nbsp;<LoadingBars/></> }
        </h2>
      </div>
      {
        data ?
        <div className="sv3-table-container">
          <div className="sv3-table-viewer">
            <table className="sv3-table">
              {
                data?.meta?.fields?.length > 0 ?
                <thead className="sv3-table-head">
                  <tr>{ data?.meta?.fields.map((col, i) => <td key={`head-${i}`}>{col}</td>) }</tr>
                </thead>
                :
                null
              }
              <tbody className="sv3-table-body">
                {
                  data?.data?.length > 0 ?
                  data?.data.map((elem, i) => {
                    let row = Object.values(elem)
                    if(row.length > 0) {
                      return (<tr key={`row-${i}`}>{ row.map((col, j) => <td key={`col-${i}-${j}`}>{col}</td>) }</tr>)
                    } else {
                      return null
                    }
                  })
                  :
                  null
                }
              </tbody>
            </table>
          </div>
        </div>
        :
        null
      }
    </>
  )
}

const readTableFromURL = ({
  url, 
  config={ download:true, header: true, skipEmptyLines: true, preview: 30 }
}) => {
  return new Promise((resolve, reject) => {
    readRemoteFile(
      url, {
        ...config,
        complete: (results, file) => {
          resolve({results, file})
        },
        error: (error, file) => {
          reject({error, file})
        }
      }
    )
  })
}

const readExcelFromURL = ({
  url,
  config = { header: true, skipEmptyLines: true, preview: 30 }
}) => {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.arrayBuffer();
      })
      .then(arrayBuffer => {
        try {
          // Parse with SheetJS
          const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });
          
          // Get first worksheet by default
          const sheetName = config.sheetName || workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Extract headers first if header option is true
          let headers = [];
          if (config.header) {
            // Get the headers from the first row
            const headerRow = XLSX.utils.sheet_to_json(worksheet, { header: 1, range: { s: { r: 0, c: 0 }, e: { r: 0, c: 999 } } })[0];
            headers = headerRow || [];
          }
          
          // Define range for data - start from second row if we have headers
          const startRow = config.header ? 1 : 0;
          let range;
          if (config.preview) {
            // Create a range that excludes the header row if headers are used
            const endRow = startRow + (config.preview - (config.header ? 1 : 0));
            range = { s: { r: startRow, c: 0 }, e: { r: endRow, c: 999 } };
          } else {
            range = config.header ? { s: { r: 1, c: 0 }, e: { r: 999999, c: 999 } } : undefined;
          }
          
          // Convert to JSON with options
          const jsonOptions = {
            header: headers.length > 0 ? headers : undefined,  // Use the extracted headers
            range: range,
            defval: config.skipEmptyLines ? undefined : ''
          };
          
          const data = XLSX.utils.sheet_to_json(worksheet, jsonOptions);
          
          // Format to match Papaparse's structure
          const results = {
            data: data,
            errors: [],
            meta: {
              fields: headers,  // Put headers in meta.fields
              delimiter: ",",   // Excel doesn't have a delimiter but we match the API
              linebreak: "\n",  // same as above
              aborted: false,
              truncated: false,
              cursor: data.length
            }
          };
          
          resolve({ results, file: { name: url.split('/').pop() } });
        } catch (error) {
          reject({ error, file: { name: url.split('/').pop() } });
        }
      })
      .catch(error => {
        reject({ error, file: { name: url.split('/').pop() } });
      });
  });
};

export default TableViewer